/* Progressive enhancement for native product-page photographs and size facts. */
(() => {
  'use strict';
  if (window.PVProductGallery) { window.PVProductGallery.init(document); return; }

  const instances = new Map();
  const PAGE = '[data-pv-product-page]';
  const indexOf = value => {
    if (typeof value !== 'string' || !/^(0|[1-9]\d*)$/.test(value)) return -1;
    const index = Number(value);
    return Number.isSafeInteger(index) ? index : -1;
  };
  const hashId = hash => {
    try { return decodeURIComponent(hash.slice(1)); } catch (_) { return ''; }
  };
  const replaceURL = url => {
    if (url.href === location.href) return;
    // History can be unavailable in an embedded preview. Selection still works.
    try { history.replaceState(history.state, '', url); } catch (_) { /* Keep the visible selection usable. */ }
  };
  const plainClick = (event, link) => !event.defaultPrevented && event.button === 0 &&
    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
    !link.hasAttribute('download') && (!link.target || link.target.toLowerCase() === '_self');

  function enhance(root) {
    const controller = new AbortController();
    const restores = [];
    const remember = (element, attributes) => {
      if (!element) return;
      attributes.forEach(name => {
        const value = element.getAttribute(name);
        restores.push(() => value === null ? element.removeAttribute(name) : element.setAttribute(name, value));
      });
    };
    const on = (element, type, handler) => element.addEventListener(type, handler, { signal: controller.signal });
    const gallery = root.querySelector('[data-product-gallery]');
    const photos = gallery ? Array.from(gallery.querySelectorAll('[data-product-photo]')) : [];
    const thumbnails = gallery ? Array.from(gallery.querySelectorAll('a[data-product-thumbnail]')) : [];
    const status = gallery && gallery.querySelector('[data-product-photo-status]');
    const thumbWrap = gallery && gallery.querySelector('[data-product-thumbnails]');
    let syncPhotos = () => {};

    if (photos.length) {
      remember(gallery, ['class']);
      photos.forEach(photo => remember(photo, ['hidden']));
      thumbnails.forEach(link => remember(link, ['aria-current']));
      remember(status, ['hidden']);
      if (status) {
        const originalText = status.textContent;
        restores.push(() => { status.textContent = originalText; });
      }
      const thumbnailIndex = link => {
        const index = indexOf(link.dataset.photoIndex);
        if (index < 0 || index >= photos.length || !photos[index].id) return -1;
        let url;
        try { url = new URL(link.getAttribute('href'), location.href); } catch (_) { return -1; }
        if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return -1;
        return hashId(url.hash) === photos[index].id ? index : -1;
      };
      const selectPhoto = (index, updateURL = false) => {
        photos.forEach((photo, current) => { photo.hidden = current !== index; });
        thumbnails.forEach(link => {
          if (thumbnailIndex(link) === index) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
        if (status) {
          status.textContent = `Photo ${index + 1} of ${photos.length} · Open image to view full size`;
          status.hidden = false;
        }
        if (updateURL && photos[index].id) {
          const url = new URL(location.href);
          url.hash = photos[index].id;
          replaceURL(url);
        }
      };
      syncPhotos = () => {
        const requested = photos.findIndex(photo => photo.id && photo.id === hashId(location.hash));
        selectPhoto(requested >= 0 ? requested : 0);
      };
      // Native anchors retain keyboard activation and opening in another tab.
      // Do not add key handlers, button roles, or a product/image dialog.
      on(thumbWrap || gallery, 'click', event => {
        const link = event.target.closest && event.target.closest('a[data-product-thumbnail]');
        if (!link || !thumbnails.includes(link) || !plainClick(event, link)) return;
        const index = thumbnailIndex(link);
        if (index < 0) return;
        event.preventDefault();
        selectPhoto(index, true);
      });
      gallery.classList.add('is-enhanced');
      syncPhotos();
    }

    const controls = root.querySelector('[data-product-variants]');
    const variants = controls ? Array.from(controls.querySelectorAll('button[data-product-variant]')) : [];
    const facts = Array.from(root.querySelectorAll('[data-product-variant-facts]'));
    const sizeRows = Array.from(root.querySelectorAll('[data-product-size-row]'));
    const completeVariants = variants.length > 0 && variants.every((button, index) =>
      indexOf(button.dataset.variantIndex) === index && facts.some(panel => indexOf(panel.dataset.variantIndex) === index));
    let syncVariants = () => {};

    if (completeVariants) {
      remember(controls, ['hidden']);
      variants.forEach(button => remember(button, ['aria-pressed']));
      facts.forEach(panel => remember(panel, ['hidden']));
      sizeRows.forEach(row => remember(row, ['class', 'aria-current']));
      const selectVariant = (index, updateURL = false) => {
        variants.forEach((button, current) => button.setAttribute('aria-pressed', String(current === index)));
        facts.forEach(panel => { panel.hidden = indexOf(panel.dataset.variantIndex) !== index; });
        sizeRows.forEach(row => {
          const selected = indexOf(row.dataset.variantIndex ?? row.dataset.productSizeRow) === index;
          row.classList.toggle('selected-size', selected);
          if (selected) row.setAttribute('aria-current', 'true');
          else row.removeAttribute('aria-current');
        });
        if (updateURL) {
          const url = new URL(location.href);
          url.searchParams.set('variant', String(index));
          replaceURL(url);
        }
      };
      syncVariants = () => {
        const values = new URL(location.href).searchParams.getAll('variant');
        const index = values.length === 1 ? indexOf(values[0]) : -1;
        selectVariant(index >= 0 && index < variants.length ? index : 0);
      };
      on(controls, 'click', event => {
        const button = event.target.closest && event.target.closest('button[data-product-variant]');
        if (!button || !variants.includes(button) || button.disabled || event.defaultPrevented) return;
        selectVariant(indexOf(button.dataset.variantIndex), true);
      });
      controls.hidden = false;
      syncVariants();
    }

    root.querySelectorAll('[data-product-video]').forEach(marker => {
      const video = marker.matches('video') ? marker : marker.querySelector('video');
      if (!video) return;
      const error = marker.querySelector('[data-product-video-error]') ||
        (video.parentElement && video.parentElement.querySelector('[data-product-video-error]')) ||
        root.querySelector('[data-product-video-error]');
      if (!error) return;
      remember(error, ['hidden']);
      const showError = () => { error.hidden = false; };
      on(video, 'error', showError);
      if (video.error) showError();
    });

    instances.set(root, {
      sync: () => { syncPhotos(); syncVariants(); },
      destroy: () => {
        controller.abort();
        restores.reverse().forEach(restore => restore());
        instances.delete(root);
      }
    });
  }

  function init(scope = document) {
    instances.forEach((instance, root) => { if (!root.isConnected) instance.destroy(); });
    const roots = Array.from(scope.querySelectorAll(PAGE));
    if (scope.matches && scope.matches(PAGE)) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) instances.get(root).sync();
      else enhance(root);
    });
  }
  function unload(scope) {
    instances.forEach((instance, root) => {
      if (scope === root || scope.contains(root)) instance.destroy();
    });
  }
  window.PVProductGallery = { init, unload };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  window.addEventListener('popstate', () => init());
  window.addEventListener('pageshow', () => init());
  window.addEventListener('hashchange', () => init());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  init();
})();
